import { apply, celebrate, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { m, type Messages } from '../i18n';
import { sfx } from '../audio/sfx';
import { say as speak, VOICES, type Voice } from '../audio/speech';
import { say } from '../ui/dialog';
import { $ } from '../ui/dom';
import { layers } from '../ui/layers';

type CallKey = keyof Messages['calls']['pool'];
type EventCallKey = keyof Messages['sky']['calls'];
type CallText = Messages['calls']['pool'][CallKey];

interface CallDef {
  key: CallKey | EventCallKey;
  /** Where the call's texts live (random calls in `calls.pool`, event calls in `sky.calls`). */
  text: () => CallText;
  voice: Voice;
  /** Stars for the good answer (once per day). */
  reward: number;
  /** Stars lost for the bad answer. */
  penalty: number;
  momLove?: [good: number, bad: number];
  /** Both answers are fine (e.g. a friend's invitation). */
  bothOk?: boolean;
}

const pool = (key: CallKey) => () => m().calls.pool[key];
const event = (key: EventCallKey) => () => m().sky.calls[key];

const POOL: CallDef[] = [
  { key: 'scamStock', text: pool('scamStock'), voice: VOICES.sales, reward: 10, penalty: 5 },
  { key: 'scamGame', text: pool('scamGame'), voice: VOICES.sales, reward: 10, penalty: 15 },
  { key: 'insurance', text: pool('insurance'), voice: VOICES.friend, reward: 5, penalty: 0 },
  { key: 'mom', text: pool('mom'), voice: VOICES.mom, reward: 8, penalty: 0, momLove: [15, -5] },
  { key: 'friend', text: pool('friend'), voice: VOICES.friend, reward: 5, penalty: 0, bothOk: true },
];

/** Calls rung by events (typhoon, earthquake, first rain), never at random. */
const EVENT_CALLS: CallDef[] = [
  { key: 'momTyphoon', text: event('momTyphoon'), voice: VOICES.mom, reward: 6, penalty: 0, momLove: [12, -6] },
  { key: 'momQuake', text: event('momQuake'), voice: VOICES.mom, reward: 6, penalty: 0, momLove: [12, -6] },
  { key: 'momRain', text: event('momRain'), voice: VOICES.mom, reward: 4, penalty: 0, momLove: [8, -4] },
];
/** Event calls waiting for the phone to be free (a dialog open, another call ringing). */
const queued: CallDef[] = [];

const MIN_GAP = 70; // real seconds between calls
let sinceLast = 30;
let ringing: CallDef | null = null;
let ringTimer = 0;

export function initCalls(): void {
  $('btnAnsCall').onclick = answer;
  $('btnRejCall').onclick = () => decline();
}

/** Called every live frame; occasionally rings the phone during waking hours. */
export function callsTick(dt: number): void {
  const s = S();
  if (ringing) {
    ringTimer += dt;
    if (ringTimer > 12) decline(true);
    return;
  }
  if (queued.length && !layers.blocking()) return ring(queued.shift()!);
  sinceLast += dt;
  if (sinceLast < MIN_GAP || s.time < 8 * 60 + 30 || s.time > 21 * 60) return;
  if (Math.random() < dt / 40) ring(POOL[Math.floor(Math.random() * POOL.length)]);
}

function ring(def: CallDef): void {
  ringing = def;
  ringTimer = 0;
  sinceLast = 0;
  sfx('ring');
  const c = def.text();
  $('incomingTitle').textContent = m().calls.incoming;
  $('incomingCaller').textContent = c.caller;
  $('incomingDesc').textContent = c.desc;
  $('btnAnsCall').textContent = `${m().calls.answer} 🟢`;
  $('btnRejCall').textContent = `${m().calls.decline} 🔴`;
  $('incoming').classList.add('show');
}

function hideBanner(): void {
  $('incoming').classList.remove('show');
}

function decline(silent = false): void {
  if (!ringing) return;
  ringing = null;
  hideBanner();
  if (silent !== true) {
    sfx('click');
    toast(m().calls.declined);
  }
}

function answer(): void {
  const def = ringing;
  if (!def || layers.blocking()) return;
  ringing = null;
  hideBanner();
  sfx('ok');
  const c = def.text();
  speak(c.intro, def.voice);
  const outcome = (good: boolean) => () => {
    const msg = good ? def.text().goodMsg : def.text().badMsg;
    toast(msg);
    if (good || def.bothOk) {
      celebrate();
      const first = onceToday(`call.${def.key}`);
      apply({ stars: first ? def.reward : 0, mood: 4, love: def.momLove ? { mom: def.momLove[0] } : undefined });
    } else {
      sfx('bad');
      apply({ stars: -def.penalty, mood: -10, love: def.momLove ? { mom: def.momLove[1] } : undefined });
    }
  };
  const choices = [
    { text: c.good, run: outcome(true) },
    { text: c.bad, run: outcome(false) },
  ];
  // Don't always put the right answer first.
  if (Math.random() < 0.5) choices.reverse();
  say({ who: c.caller, icon: '📞', text: `“${c.intro}”`, choices });
}

export function isRinging(): boolean {
  return ringing !== null;
}

/** Ring a specific call right now (events: typhoon, earthquake, parents' meeting…). */
export function ringNow(key: string): void {
  const def = EVENT_CALLS.find((d) => d.key === key) ?? POOL.find((d) => d.key === key);
  if (!def || ringing === def || queued.includes(def)) return;
  // Never lose an event call: wait until the phone and the screen are free.
  if (ringing || layers.blocking()) queued.push(def);
  else ring(def);
}
